// @flow
import * as routePaths from '~/core/constants/routes';

import type { Config } from '~/core/types';
import amazonAppstoreImage from '~/core/icons/amazon-appstore-default.svg';
import { deviceTypes } from '~/core/constants/devices';
import generalIcons from '~/core/constants/icons/general-svg';
import { menuTemplates } from '~/core/constants/menu';
import { DISCOVER_FEATURES } from '~/core/state/app-settings/constants';

const config: Config = {
    actions: {
        defaultSchedule: {
            monday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            tuesday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            wednesday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            thursday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            friday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            saturday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            sunday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ]
        },
        requiredDeviceUrl: {
            'contact-sensor': 'https://www.hivehome.com/products/hive-window-door-sensor',
            'hive-camera-sensor': 'https://www.hivehome.com/products/hive-view',
            'motion-sensor': 'https://www.hivehome.com/products/hive-motion-sensor',
            'na-temperature': 'https://www.hivehome.com/us/products/hive-active-thermostat',
            temperature: 'https://www.hivehome.com/products/hive-active-heating'
        },
        templatesVersion: 1
    },
    acquireDevices: {
        attemptsBeforeWarning: 5,
        attemptsBeforeTroubleshooting: 10,
        pollExpiry: 600000,
        pollInterval: 5000
    },
    alerts: {
        expiry: 5000
    },
    confirmHubDiscovery: false,
    cannotRemoveDevice: {
        hub: deviceTypes.HUB,
        leakSensor: deviceTypes.LEAK_SENSOR,
        naThermostat: deviceTypes.NA_THERMOSTAT
    },
    features: {
        activeplugPowerConsumption: false,
        batteryAlerts: true,
        dev: false,
        dashboardCustomisation: true,
        dashboardCustomisationData: true, // Used in place of dashboardCustomisation until features are completed
        deviceGroups: false,
        devicegroupSchedule: false,
        emergencyHeat: true,
        helpAndSupportMenuLink: true,
        holidayMode: true,
        hueSchedule: false,
        hueMimic: false,
        installAddZone: true,
        installCamera: true,
        installHiveHub: true,
        installHiveHub360: false,
        installHiveView: false,
        installLeakSensor: false,
        installOtherDevices: true,
        installPhilipsHue: false,
        installNano1Upgrade: false,
        installNano1Upgrade360: false,
        installUpgradeHive2: false,
        installZones: true,
        leakSensor: true,
        mimicMode: true,
        mimicModeUpsell: true,
        mySubscriptions: false,
        newActions: true,
        notifications: true,
        readyBy: false, // actual Ready By feature
        readyByOnboarding: false, // setting up Ready By that is not launching with Ready By for now
        recipes: true,
        shopMenuLink: true,
        textControl: true,
        upgradeNano1Hub: false,
        setPin: true,
        hotWaterAdvance: true,
        hiveButton: false,
        manageDevicesModal: true,
        serviceOutageStatusCheck: false,
        showPhoneNumber: true,
        multiHome: false,
        slt3Replacement: false,
        identifyProductButton: false
    },
    discoverNewFeatures: {
        [DISCOVER_FEATURES.ACTIONS2]: true,
        [DISCOVER_FEATURES.GROUPS]: false,
        [DISCOVER_FEATURES.HUE]: false
    },
    footer: {
        showDetailedFooter: false
    },
    help: {
        amazonAppstoreImage,
        amazonAppstoreLink: 'https://www.amazon.co.uk/gp/mas/dl/android?asin=B00KLZFPYK',
        createAccountLink: 'https://www.hivehome.com/register',
        helpLink: 'https://www.hivehome.com/user-guides',
        hiveActions: 'https://www.hivehome.com/actions',
        mainLink: 'https://www.hivehome.com/',
        supportHours: '8am–9pm seven days a week',
        // eslint-disable-next-line max-len
        faqLink:
            'https://www.hivehome.com/support/Help_Using_Hive/HUH_General/How-to-amend-schedules-using-the-website-dashboard',
        mainLinkText: 'hivehome.com',
        termsConditionsLink: 'https://www.hivehome.com/terms',
        hubPlacementGuide:
            'https://www.hivehome.com/faqs/Help_installing_Hive/HIH_General/Where-is-the-best-place-to-position-my-Hive-Hub',
        itunesLink: 'https://itunes.apple.com/gb/app/hive/id712829172?mt=8',
        googlePlayLink: 'https://play.google.com/store/apps/details?id=uk.co.centrica.hive&hl=en_GB',
        webshopUrl: 'https://www.hivehome.com/products/categories/lights'
        // eslint-disable-line max-len
    },
    holidayMode: {
        emergencyHeatTemperature: 25,
        temperatureWarningValue: 12.5,
        singleEditor: true
    },
    iconLocaleSuffix: 'UK',
    installZones: {
        attemptsBeforeWarning: 5,
        attemptsBeforeTroubleshooting: 10,
        pollExpiry: 600000,
        pollInterval: 5000
    },
    temperatureUnit: 'C',
    menu: [
        {
            id: 'menu.home',
            icon: generalIcons.DASHBOARD,
            subItems: [
                {
                    id: 'menu.home.overview',
                    link: routePaths.dashboard
                },
                {
                    id: 'menu.home.manage',
                    link: routePaths.manageHome
                }
            ]
        },
        {
            icon: generalIcons.DASHBOARD,
            id: 'menu.dashboard',
            link: routePaths.dashboard
        },
        {
            icon: generalIcons.ACTIONS,
            id: 'menu.recipes',
            link: routePaths.recipes,
            conditions: {
                canUseActions: true
            }
        },
        {
            icon: generalIcons.SETTINGS,
            id: 'menu.settings',
            subItems: [
                {
                    id: 'menu.settings-manage-devices',
                    link: routePaths.devices,
                    conditions: {
                        hasManageableDevices: true
                    }
                },
                {
                    id: 'menu.settings-install',
                    link: routePaths.install
                },
                {
                    id: 'menu.my-account',
                    link: routePaths.myAccount
                },
                {
                    id: 'menu.settings-change-password',
                    link: routePaths.changePassword
                },
                {
                    id: 'menu.settings-set-pin',
                    link: routePaths.changePin,
                    conditions: {
                        feature: 'setPin'
                    }
                },
                {
                    id: 'menu.settings-notifications',
                    link: routePaths.notifications,
                    conditions: {
                        feature: 'notifications',
                        hasHub: true,
                        hasHeating: true,
                        hasActions1: true
                    }
                },
                {
                    id: 'menu.settings-text-control',
                    link: routePaths.textControl,
                    conditions: {
                        feature: 'textControl',
                        hasHub: true,
                        hasHeating: true,
                        hasPMZ: false
                    }
                },
                {
                    id: 'menu.settings-logout',
                    onClick: props => props.logOut()
                }
            ]
        },
        {
            icon: generalIcons.HELP,
            id: 'menu.help',
            staticLink: {
                url: 'https://www.hivehome.com/support',
                target: '_blank'
            },
            conditions: {
                feature: 'helpAndSupportMenuLink'
            }
        },
        {
            icon: generalIcons.SHOP,
            id: 'menu.shop',
            template: menuTemplates.SHOP,
            conditions: {
                feature: 'shopMenuLink'
            },
            staticLink: {
                url: 'https://www.hivehome.com/products'
            }
        },
        {
            icon: generalIcons.DEMO,
            id: 'Dev',
            link: '/dev',
            conditions: {
                feature: 'dev'
            }
        }
    ],
    notifications: {
        defaults: {
            hotEnabled: false,
            hotTemperature: 25,
            coldEnabled: false,
            coldTemperature: 10,
            warningsEnabled: false
        },
        minTemp: 5,
        maxTemp: 32
    },
    products: {
        bulbs: {
            maxTemperature: 6535,
            minTemperature: 2700,
            offWhiteHue: 23,
            offWhiteSaturation: 20
        },
        colourtuneablelight: {
            defaultBrightness: 100,
            defaultHue: 0,
            defaultSaturation: 99,
            defaultTemperature: 2700
        },
        colourlight: {
            defaultBrightness: 100,
            defaultHue: 0,
            defaultSaturation: 99,
            defaultTemperature: 2700
        },
        cooling: {
            defaultTemp: 26,
            interval: 0.5,
            maxTemp: 32,
            metric: 'C',
            defaultBoostTarget: 25.5,
            defaultBoostDuration: 60,
            minTemp: 16.5
        },
        heating: {
            defaultTemp: 20,
            frostProtection: 7,
            interval: 0.5,
            maxTemp: 32,
            maxTempDual: 30.5,
            metric: 'C',
            minTemp: 5,
            defaultBoostDuration: 60,
            defaultBoostTarget: 22
        },
        hotwater: {
            defaultBoostDuration: 60
        },
        tuneablelight: {
            defaultBrightness: 100,
            defaultTemperature: 2700
        },
        warmwhitelight: {
            defaultBrightness: 80
        }
    },
    recipes: {
        defaultSchedule: {
            monday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            tuesday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            wednesday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            thursday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            friday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            saturday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ],
            sunday: [
                { start: 390, value: { state: 'ARM' } },
                { start: 510, value: { state: 'ARM' } },
                { start: 960, value: { state: 'ARM' } },
                { start: 1290, value: { state: 'ARM' } }
            ]
        },
        notifications: {
            email: true,
            push: true,
            sms: false
        }
    },
    subscriptions: {
        hiveLiveUrl: 'https://www.hivehome.com/products/hive-live'
    },
    upgradeThermostat: {
        pollExpiry: 300000
    },
    upgradeNano1Hub: {
        available: true
    },
    user: {
        inactiveTimeout: 1200000
    },
    v2JWTVerifyKey: '26378263789',
    weather: {}
};

export default config;



// WEBPACK FOOTER //
// ./src/core/config/default.js